import { session } from "../auth/session";
import { getDeploymentConfig } from "./deployment-config";
import { isAbort, RequestTimeoutError } from "@jkast90/kast-common/fetch";
import { fetchWithLogging, type FetchOptions } from "./fetch-logger";
import { needsConnection } from "@jkast90/kast-common/offline";

// The one way to call play-api (api/). Every route is behind the shared
// Cognito authorizer and a pocket-play group check, so every call carries
// the session's bearer; refusals come back as {"detail": "..."}.

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
  ) {
    super(message);
  }
}

/**
 * No one is signed in, so the call was never sent. Not a 401: nothing
 * expired, and the query client clears its cache on a 401.
 */
export class SignedOutError extends ApiError {
  constructor() {
    super("Sign in to use cloud features.");
  }
}

/**
 * The browser has no network, so the call was never sent. Pages show its
 * message where the cloud's data would be; nothing local depends on it.
 */
export class OfflineError extends ApiError {
  readonly needsConnection = true;
  constructor() {
    super("You're offline: the cloud needs a connection, and comes back when you are.");
  }
}

/** The HTTP status an API error carries, for the query client. */
export const statusOf = (error: unknown): number | undefined => (error instanceof ApiError ? error.status : undefined);

/** Whether signing in (again) is what the error needs: signed out, or a sign-in that expired. */
export const needsSignIn = (error: unknown) => error instanceof SignedOutError || statusOf(error) === 401;

/**
 * For a route that does slow work server-side (a log query, an S3 copy, a
 * fetch from GitHub). API Gateway gives up at 29 s, so waiting longer is
 * pointless.
 */
export const SLOW_CALL: FetchOptions = { timeoutMs: 30_000 };

// An expired token is a 401; a token from before a group change, a 403.
// Either is refreshed once and the call retried, as the session decides
// (onUnauthorized, onForbidden): not when the tokens were issued moments
// ago, so the refusal is real, and a 403 never signs out. That is the only retry,
// since the server never saw a request it answered 401 or 403. Nothing
// else is retried here (a POST may not be safe to repeat); the query
// client retries its reads. Each attempt has its own
// time limit (8 s unless `options` says otherwise); a call the caller
// cancelled through `init.signal` rejects with that abort, untouched.
export async function apiRequest(path: string, init: RequestInit = {}, options: FetchOptions = {}): Promise<Response> {
  return attempt(path, init, options, false);
}

async function attempt(path: string, init: RequestInit, options: FetchOptions, retried: boolean): Promise<Response> {
  const token = session.bearer();
  if (!token) throw new SignedOutError();
  needsConnection(() => new OfflineError());
  let response: Response;
  try {
    response = await fetchWithLogging(
      `${getDeploymentConfig().apiUrl}${path}`,
      { ...init, headers: { Authorization: `Bearer ${token}`, ...init.headers } },
      options,
    );
  } catch (error) {
    if (error instanceof RequestTimeoutError)
      throw new ApiError("The cloud didn't answer in time. Check your connection and try again.");
    if (init.signal?.aborted && isAbort(error)) throw error;
    needsConnection(() => new OfflineError());
    throw new ApiError("The cloud is unreachable right now.");
  }
  if (!retried && (await worthRetrying(response.status, token))) return attempt(path, init, options, true);
  if (response.status === 401) throw new ApiError("Your sign-in has expired. Sign in again.", 401);
  if (response.status === 403) throw new ApiError("This account isn't allowed to use cloud features.", 403);
  if (!response.ok) {
    const detail = await response
      .json()
      .then((body: { detail?: string }) => body.detail)
      .catch(() => undefined);
    throw new ApiError(detail ?? `Request failed (${response.status}).`, response.status);
  }
  return response;
}

/** After a 401 or 403 on `token`: whether new tokens are in place to try again with. */
async function worthRetrying(status: number, token: string): Promise<boolean> {
  if (status === 401) return session.onUnauthorized(token);
  if (status === 403) return session.onForbidden(token);
  return false;
}
